import React from "react";
import { EnvelopeIcon } from "@heroicons/react/24/outline";

interface EmailButtonProps {
    to?: string;       // comma-separated list of recipients
    cc?: string;       // optional CC (comma-separated)
    bcc?: string;      // optional BCC (comma-separated)
    subject?: string;
    body?: string;
    label?: string;    // tooltip or accessible label
}

function encodeAddrList(list?: string) {
    if (!list) return "";
    return list
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
        .map(encodeURIComponent)
        .join(",");
}

function buildMailtoHref({ to, cc, bcc, subject, body }: EmailButtonProps) {
    const parts: string[] = [];
    if (cc) parts.push(`cc=${encodeURIComponent(cc)}`);
    if (bcc) parts.push(`bcc=${encodeURIComponent(bcc)}`);
    if (subject) parts.push(`subject=${encodeURIComponent(subject)}`);
    if (body) {
        const normalized = body.replace(/\r?\n/g, "\r\n"); // better client compatibility
        parts.push(`body=${encodeURIComponent(normalized)}`);
    }
    const query = parts.length ? `?${parts.join("&")}` : "";
    const addr = encodeAddrList(to);
    return `mailto:${addr}${query}`;
}

export const EmailButton: React.FC<EmailButtonProps> = (props) => {
    const handleClick = async () => {
        // Optional clipboard fallback if a client strips body
        if (props.body && navigator.clipboard?.writeText) {
            try { await navigator.clipboard.writeText(props.body); } catch {}
        }
        window.location.href = buildMailtoHref(props);
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            className="rounded-md bg-green-600 p-1 text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
            title={props.label ?? "Send Email"}
        >
            <EnvelopeIcon className="h-4 w-5" aria-hidden="true" />
        </button>
    );
};
